/**
 * `pending-shared-ui-primitives` v0-minimal — `<Input />`.
 *
 * Token-driven; same `tokens.css` requirement as `<Button />`.
 *
 * v0 explicitly does NOT do:
 *   - Built-in label slot (caller wraps with their own label —
 *     react-hook-form / formik / RHF integrations stay external).
 *   - Inline error / hint slots (v0.1).
 *   - Multi-line `<textarea>` variant (v0.1; may stay separate).
 */

import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  invalid?: boolean;
  fullWidth?: boolean;
}

const SIZE_CLASSNAME: Record<InputSize, string> = {
  sm: 'rui-input--sm',
  md: 'rui-input--md',
  lg: 'rui-input--lg',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const { inputSize = 'md', invalid = false, fullWidth = false, className, type, ...rest } = props;
  const cls = [
    'rui-input',
    SIZE_CLASSNAME[inputSize],
    invalid ? 'rui-input--invalid' : null,
    fullWidth ? 'rui-input--full' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <input ref={ref} type={type ?? 'text'} className={cls} aria-invalid={invalid || undefined} {...rest} />;
});
